import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router";
import { listWorkspaces } from "@/lib/api/endpoints";
import { useCurrentUser, useSession } from "@/lib/auth/session";
import { PageLoading, useLoader, useRevalidate } from "@/lib/loader";
import {
  readActiveCustomerFromDocument,
  writeActiveCustomerCookie,
} from "@/lib/mapper/active-customer-cookie";
import { AppShell } from "./AppShell";
import { BackendUnavailable } from "./BackendUnavailable";
import { CreateWorkspace } from "./CreateWorkspace";

/**
 * Authenticated shell. Loads the user's workspaces and keeps the
 * active-workspace cookie valid (the API client sends it as x-workspace-id),
 * defaulting to the first workspace when it's unset or stale. Users with no
 * workspace get the create-workspace screen instead of the page.
 */
export function AppLayout() {
  const user = useCurrentUser();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const revalidate = useRevalidate();
  const workspaces = useLoader(() => listWorkspaces(), []);

  const list = workspaces.data;
  const activeId = readActiveCustomerFromDocument();
  const activeValid = !list || list.length === 0 || list.some((w) => w.id === activeId);

  useEffect(() => {
    if (list && list.length > 0 && !activeValid) {
      writeActiveCustomerCookie(list[0].id);
      revalidate();
    }
  }, [list, activeValid, revalidate]);

  if (workspaces.status === "error" && !list) return <BackendUnavailable />;
  if (!list || !activeValid) return <PageLoading />;

  return (
    <AppShell
      user={{ name: user.name, email: user.email, image: user.image }}
      role={user.role}
      onSignOut={() => {
        void signOut().then(() => navigate("/login", { replace: true }));
      }}
    >
      {list.length === 0 ? <CreateWorkspace onCreated={revalidate} /> : <Outlet />}
    </AppShell>
  );
}
